/**
 * API Configuration Module
 *
 * Centralized configuration for all API endpoints and network requests.
 * Uses environment variables to support different deployment environments.
 *
 * Usage:
 *   import { apiFetch, API_CONFIG } from './config/api';
 *
 *   // For direct URL construction:
 *   const url = getApiUrl('/pipelines/parse');
 *
 *   // For enhanced fetch with error handling:
 *   const response = await apiFetch('/pipelines/parse', {
 *     method: 'POST',
 *     body: JSON.stringify(data)
 *   });
 *
 * Environment Variables Required:
 *   - REACT_APP_API_BASE_URL: Base URL for API (e.g., https://api.yourapp.com)
 *   - REACT_APP_WS_URL: WebSocket URL (optional)
 *   - REACT_APP_ENV: Environment name (development/production)
 */

// Get base API URL from environment variables with fallback
const getApiBaseUrl = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    if (!baseUrl) {
        console.warn('REACT_APP_API_BASE_URL not set, falling back to localhost:8000');
        return 'http://localhost:8000';
    }

    return baseUrl;
};

// API Configuration object
export const API_CONFIG = {
    BASE_URL: getApiBaseUrl(),
    ENDPOINTS: {
        PIPELINES: {
            PARSE: '/pipelines/parse',
            SUBMIT: '/pipelines/submit',
            VALIDATE: '/pipelines/validate'
        },
        NODES: {
            LIST: '/nodes',
            CREATE: '/nodes',
            UPDATE: '/nodes'
        }
    },
    WEBSOCKET_URL: process.env.REACT_APP_WS_URL || 'ws://localhost:8000/ws'
};

/**
 * Constructs a full API URL from an endpoint path
 * @param {string} endpoint - The endpoint path (e.g., '/pipelines/parse')
 * @returns {string} - Full URL
 */
export const getApiUrl = (endpoint) => {
    const baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, ''); // Remove trailing slash
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${baseUrl}${path}`;
};

/**
 * Default fetch configuration with common headers
 */
export const DEFAULT_FETCH_CONFIG = {
    headers: {
        'Content-Type': 'application/json',
    }
};

/**
 * Enhanced fetch wrapper with environment-aware error handling
 * @param {string} endpoint - API endpoint path
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const apiFetch = async (endpoint, options = {}) => {
    const url = getApiUrl(endpoint);
    const config = {
        ...DEFAULT_FETCH_CONFIG,
        ...options,
        headers: {
            ...DEFAULT_FETCH_CONFIG.headers,
            ...(options.headers || {})
        }
    };

    try {
        const response = await fetch(url, config);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        // Enhanced error handling for different environments
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            const env = process.env.REACT_APP_ENV || 'development';
            if (env === 'development') {
                throw new Error('Failed to connect to backend server. Please ensure the backend is running on port 8000.');
            } else {
                throw new Error('API service is currently unavailable. Please try again later.');
            }
        }
        throw error;
    }
};

/**
 * Validates API configuration on app startup
 */
export const validateApiConfig = () => {
    const requiredVars = ['REACT_APP_API_BASE_URL'];
    const missing = requiredVars.filter(varName => !process.env[varName]);

    if (missing.length > 0) {
        console.warn(`Missing environment variables: ${missing.join(', ')}`);
        console.warn('Using default localhost configuration');
    }

    console.log('API Configuration:', {
        baseUrl: API_CONFIG.BASE_URL,
        environment: process.env.REACT_APP_ENV || 'development',
        websocketUrl: API_CONFIG.WEBSOCKET_URL
    });
};

// Validate configuration when module is imported
validateApiConfig();